import axios from 'axios'
import { toast } from '@/utils/toast'
import store from '@/store'
import { clearAuthState } from '@/utils/login'

const BASE_API = import.meta.env.VITE_APP_BASE_API
  || import.meta.env.VUE_APP_BASE_API
  || '/insights'

const service = axios.create({
  baseURL: BASE_API,
  timeout: 30000,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json;charset=utf-8' },
})

// Guard against repeated session-expiry handling. A single expired
// session usually triggers errors on multiple in-flight requests at
// once (e.g. router guard probe + a CloudWatch query); we only want
// to dispatch LogOut and toast/redirect ONCE per expiry event.
let expiryHandled = false

/**
 * Handle SSO session expiry uniformly across both response success and
 * error paths. Mirrors cfes-admin-frontend-vue3:
 *   1. Toast a warning (unless we are already on the login page)
 *   2. Dispatch LogOut so the backend session cookie is invalidated
 *      (otherwise the router guard's principal probe would see the
 *      user as still authenticated and bounce them back to /home)
 *   3. Hard-redirect to '/' so the SPA fully resets its state
 */
const handleSessionExpired = () => {
  if (expiryHandled) return
  expiryHandled = true

  const onLogin = window.location.pathname === '/'
  if (!onLogin) {
    toast.warning('Authentication expired. Redirecting to login...')
  }

  // Always purge cached auth-state so the next visit to '/' falls through
  // to a real backend probe. Without this, the localStorage cache survives
  // a 401 and the router guard would treat the user as still authenticated.
  store.commit('SET_PRINCIPAL_STATE', null)
  clearAuthState()

  // Fire-and-forget backend logout to kill the server session, but ONLY
  // if we were on an authenticated page when expiry hit. If we are
  // already on '/', the user hasn't logged in yet (this is just an
  // anonymous principal probe coming back as 401) and there's no
  // session to invalidate — calling /logout would be wasted noise.
  if (!onLogin) {
    store.dispatch('LogOut').catch(() => { /* already logging */ })
    // Hard navigation (not router.replace) so the entire SPA reboots from
    // a clean slate — no stale Vuex state, no in-flight axios calls.
    window.location.href = '/'
  }
}

service.interceptors.request.use(
  (config) => config,
  (error) => Promise.reject(error)
)

/**
 * Detects a "session expired" response that was silently redirected to
 * the SSO login page.
 *
 * When the backend session cookie is invalid, Spring Security / the SSO
 * gateway responds with a 302 to the IdP login page. Browsers follow
 * that redirect transparently for XHR/fetch, so from axios' point of
 * view we get an HTTP 200 whose body is the HTML login page — not JSON.
 *
 * Symptoms without this check:
 *   - listLogGroups() succeeds with an HTML string,
 *   - normalizeLogGroups() flattens it to [],
 *   - the CloudWatchLogs page toasts "No CloudWatch log groups returned
 *     by backend" instead of redirecting to /login.
 *
 * We flag as expired when EITHER the Content-Type is text/html OR the
 * body is a string that starts with an HTML tag / doctype.
 */
const looksLikeLoginRedirect = (response) => {
  const contentType = String(
    response?.headers?.['content-type'] || response?.headers?.get?.('content-type') || ''
  ).toLowerCase()
  if (contentType.includes('text/html')) return true
  const body = response?.data
  if (typeof body === 'string') {
    const head = body.trimStart().slice(0, 15).toLowerCase()
    if (head.startsWith('<!doctype html') || head.startsWith('<html')) return true
  }
  return false
}

service.interceptors.response.use(
  (response) => {
    // Silent SSO redirect (302 -> login page HTML). See looksLikeLoginRedirect.
    if (looksLikeLoginRedirect(response)) {
      handleSessionExpired()
      return Promise.reject(new Error('Session expired'))
    }
    const data = response.data
    // Backend may signal session expiry via a body code
    if (data && (data.code === 401 || data.code === 'UNAUTHORIZED' || data.code === 'TOKEN_EXPIRED')) {
      handleSessionExpired()
      return Promise.reject(new Error('Session expired'))
    }
    if (data && data.code && data.code !== 200 && data.message) {
      toast.error(data.message)
    }
    return data ?? {}
  },
  (error) => {
    const status = error.response?.status
    const errorMessage = error.response?.data?.message || error.message

    // Don't log noisy stack-traces for the expected expiry case.
    if (status !== 401 && status !== 403) {
      console.error('[request] error', error)
    }

    // SSO token expired / not authorized.
    //
    // IMPORTANT: skip this branch for the /logout call itself — otherwise
    // a 401 from the logout endpoint would re-trigger handleSessionExpired
    // -> dispatch('LogOut') -> POST /logout -> 401 ... infinite loop.
    const isLogoutCall = (error.config?.url || '').endsWith('/logout')
    if ((status === 401 || status === 403) && !isLogoutCall) {
      handleSessionExpired()
      return Promise.reject(error)
    }

    if (status >= 500) {
      toast.error(`Server error: ${errorMessage || 'Please try again later'}`)
    } else if (status >= 400 && !isLogoutCall) {
      toast.error(errorMessage || 'Request failed')
    } else if (error.code === 'ECONNABORTED') {
      toast.error('Request timeout. Please try again.')
    } else if (!isLogoutCall) {
      toast.error(errorMessage || 'Network error. Please check your connection.')
    }

    return Promise.reject(error)
  }
)

export default async function request({ url, method = 'get', data, params, responseType, headers, baseURL }) {
  return service({ url, method, data, params, responseType, headers, baseURL })
}

